import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import { Paper, TextField, Link } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import AuthService from "../../api/AuthService";
import { useAlert } from "../../context/AlertContext";
import styles from "../../entities/ResetPasswordPage/styles";

const RESEND_COOLDOWN = 30; // seconds — OTP itself expires in 10 min server-side

// Phone verification via SMS OTP. Only reachable once the email is verified
// (the token from /verify-email/:token is required for these calls).
const VerifyPhone = () => {
  const navigate = useNavigate();
  const { showAlert } = useAlert();

  const [otp, setOtp] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [sending, setSending] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  const sentOnce = useRef(false);

  // Guard: need a session, and email must already be verified.
  useEffect(() => {
    if (!localStorage.getItem("accessToken")) {
      navigate("/");
      return;
    }
    if (localStorage.getItem("isEmailVerified") === "false") {
      navigate("/verify-email");
      return;
    }
    if (localStorage.getItem("isPhoneVerified") === "true") {
      navigate("/dashboard");
    }
  }, [navigate]);

  const sendOtp = async (isResend) => {
    setSending(true);
    try {
      const result = await AuthService.sendPhoneOtp();
      if (result?.success !== false) {
        showAlert(
          isResend
            ? "A new code has been sent to your phone."
            : "We've sent a verification code to your phone.",
          "success"
        );
        setCooldown(RESEND_COOLDOWN);
      } else {
        showAlert(result?.message || "Could not send the code. Please try again.", "error");
      }
    } catch (error) {
      showAlert(error?.message || "Could not send the code. Please try again.", "error");
    } finally {
      setSending(false);
    }
  };

  // Auto-send the first OTP on mount.
  useEffect(() => {
    if (sentOnce.current) return;
    if (!localStorage.getItem("accessToken")) return;
    sentOnce.current = true;
    sendOtp(false);
  }, []);

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const timer = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);

  const handleVerify = async () => {
    if (!otp) return;
    setVerifying(true);
    try {
      const result = await AuthService.verifyPhoneOtp(otp.trim());
      if (result?.success && result?.data?.isPhoneVerified) {
        localStorage.setItem("isPhoneVerified", "true");
        showAlert("Your phone number has been verified.", "success");
        navigate("/dashboard");
      } else {
        showAlert(result?.message || "Invalid or expired code. Please try again.", "error");
      }
    } catch (error) {
      showAlert(error?.message || "Something went wrong. Please try again.", "error");
    } finally {
      setVerifying(false);
    }
  };

  return (
    <Box sx={styles.main}>
      <Container sx={styles.root} maxWidth="sm">
        <Paper sx={styles.paper}>
          <Grid container sx={styles.gridContainer}>
            <Grid item xs={12} sx={styles.gridItem}>
              <Box sx={styles.formContain}>
                <Typography variant="h4" sx={styles.mainheading}>
                  Verify your phone
                </Typography>
                <Box sx={styles.headingborder}></Box>

                <Typography sx={styles.descriptionText}>
                  Enter the 6-digit code we sent to your phone. The code expires
                  in 10 minutes.
                </Typography>

                <Box sx={{ mb: 1 }}>
                  <Typography sx={styles.inputText}>Verification code</Typography>
                  <TextField
                    fullWidth
                    size="small"
                    value={otp}
                    onChange={(e) =>
                      setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))
                    }
                    inputProps={{ inputMode: "numeric", maxLength: 6 }}
                    placeholder="123456"
                  />
                </Box>

                <LoadingButton
                  loading={verifying}
                  onClick={handleVerify}
                  fullWidth
                  variant="contained"
                  disabled={otp.length < 4}
                  sx={styles.submit}
                >
                  Verify phone
                </LoadingButton>

                <Box sx={{ textAlign: "center", mt: 1 }}>
                  <Link
                    component="button"
                    type="button"
                    onClick={() => sendOtp(true)}
                    underline="hover"
                    disabled={sending || cooldown > 0}
                    sx={{
                      color: cooldown > 0 ? "#b0b0b0" : "#202020",
                      fontSize: "13px",
                      pointerEvents: sending || cooldown > 0 ? "none" : "auto",
                    }}
                  >
                    {cooldown > 0 ? `Resend code in ${cooldown}s` : "Resend code"}
                  </Link>
                </Box>

                <Box sx={{ textAlign: "center", mt: 1.5 }}>
                  <Link
                    component="button"
                    type="button"
                    onClick={() => navigate("/dashboard")}
                    underline="hover"
                    sx={{ color: "#878787", fontSize: "13px" }}
                  >
                    Skip for now
                  </Link>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Paper>
      </Container>
    </Box>
  );
};

export default VerifyPhone;
